import { DirectoryDto } from './../../../../data-transfer/entities/directory-dto';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { PackagingComponentTypesEnum } from './../../../../model/packaging-component-types-enum';
import { SimpleAlertDialogComponent, SimpleDialogData } from './../../../dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { ImportExportApiService } from '../../../../data-transfer/services/import-export-api-service';
import { SelectLocationDialogComponent } from './../../dialogs/select-location-dialog/select-location-dialog.component';
import { DirectoryApiService } from 'src/app/data-transfer/services/directory-api-service';
import { OverviewTableComponent } from './../../../shared-components/parent-components/overview-table/overview-table.component';
import { DeleteDialogData, DeleteItemDialogComponent } from './../../../dialogs/delete-item-dialog/delete-item-dialog.component';
import { TranslocoService } from '@jsverse/transloco';
import { ComponentApiService } from '../../../../data-transfer/services/component-api-service';
import { forkJoin, of } from 'rxjs';
import { ComponentTypeService } from './../../../../navigation/services/component-type-service';
import { ComponentNavigationService } from '../../../../navigation/services/navigation-services/component-navigation-service';
import { Component, Input, Output, EventEmitter, AfterViewInit, SimpleChanges, OnChanges, LOCALE_ID, Inject, ViewChild, OnInit } from '@angular/core';
import { PackagingComponentDto } from 'src/app/data-transfer/entities/component-entities/packaging-component-dto';
import { PermissionTypeDto } from 'src/app/data-transfer/entities/permission-type-dto';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { NgxSpinnerService } from 'ngx-spinner';
import { DeletedEntitiesDto, DeletedEntityDto } from 'src/app/data-transfer/entities/deleted-entities-dto';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { DialogActions, DictionaryHandler } from 'src/app/model/dictionary';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { PaginationFilterDto } from 'src/app/data-transfer/entities/pagination-filter-dto';
import { Sort } from '@angular/material/sort';
import { PackagingComponentPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';

@Component({
  selector: 'app-packaging-components-overview',
  templateUrl: './packaging-components-overview.component.html',
  styleUrls: ['./packaging-components-overview.component.scss']
})
export class PackagingComponentsOverviewComponent extends OverviewTableComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() dataSource: PaginationDataSource<PackagingComponentDto> | MatTableDataSource<PackagingComponentDto>;
  @Input() isUserValidator = false;
  @Input() isRecyclingBinSelected = false;
  @Input() displayedColumns: string[] = [];
  @Input() directoryId: number | undefined;
  @Input() componentCategoryId: number | undefined;
  @Input() componentSubtypeId: number | undefined;

  @Output() dataChanged = new EventEmitter();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  selectedComponentIds: number[] = [];
  private componentIds: number[] = [];
  callerLevelId = PackagingPart.Component;
  isLoading = of(false);

  constructor(
    private componentApiService: ComponentApiService,
    private componentTypeService: ComponentTypeService,
    private dictionaryHandler: DictionaryHandler,
    protected navigationService: ComponentNavigationService,
    protected translateService: TranslocoService,
    protected dialog: MatDialog,
    protected directoryApiService: DirectoryApiService,
    protected importExportApiService: ImportExportApiService,
    protected spinner: NgxSpinnerService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    super(dialog, directoryApiService, importExportApiService, navigationService, translateService, spinner, locale);
    this.dataSource = new MatTableDataSource<PackagingComponentDto>();
  }

  get isPaginatorVisible(): boolean {
    return this.dataSource instanceof PaginationDataSource;
  }

  ngOnInit(): void {
    if (this.dataSource instanceof PaginationDataSource) {
      this.isLoading = this.dataSource.isLoading;
    }
  }

  ngAfterViewInit(): void {
    if (this.dataSource instanceof PaginationDataSource) {
      this.ngAfterViewInitPagination(this.dataSource);
    } else {
      this.ngAfterViewInitStatic(this.dataSource);
    }
  }

  private ngAfterViewInitPagination(dataSource: PaginationDataSource<PackagingComponentDto>): void {
    this.refreshDataSource();
    dataSource.page.subscribe(page => {
      this.paginator.length = page.totalItemCount;
      this.paginator.pageIndex = page.pageIndex;
      this.componentIds = page.allIds;
      this.selectedComponentIds = this.selectedComponentIds.filter(id => page.allIds.includes(id));
    });
    dataSource.filteredColumns.subscribe(filteredColumns => {
      this.filteredColumns = filteredColumns;
    });
  }

  private ngAfterViewInitStatic(dataSource: MatTableDataSource<PackagingComponentDto>): void {
    dataSource.sort = this.sort;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.dataSource instanceof PaginationDataSource) {
      this.ngOnChangesPagination(changes);
    } else {
      this.ngOnChangesStatic(changes, this.dataSource);
    }
  }

  private ngOnChangesPagination(changes: SimpleChanges): void {
    if (changes.dataSource != null) {
      this.selectedComponentIds = [];
    }

    if (this.directoryId && changes.directoryId) {
      if (this.paginator) {
        this.refreshDataSource();
      }
    }
  }

  private ngOnChangesStatic(changes: SimpleChanges, dataSource: MatTableDataSource<PackagingComponentDto>): void {
    if (changes.dataSource != null) {
      super.setDataSourceFilter(dataSource);
      this.dataSourceSubscription = dataSource.connect().subscribe(_ => {
        this.updateDisplayTrackedColumn(dataSource);
      });
    }
  }

  refreshDataSource() {
    if (!(this.dataSource instanceof PaginationDataSource)) {
      return;
    }

    let filters: PaginationFilterDto[] = [];

    if (this.directoryId && !this.isRecyclingBinSelected) {
      filters = [{
        fieldName: "DirectoryId",
        value: this.directoryId.toString(),
      }];
    }

    if (this.componentSubtypeId && this.componentSubtypeId >= 0) {
      filters.push({
        fieldName: "PackagingComponentSubtype",
        value: this.componentSubtypeId.toString()
      });
    }

    if (this.componentCategoryId && this.componentCategoryId >= 0) {
      filters.push({
        fieldName: "PackagingComponentCategory",
        value: this.componentCategoryId.toString()
      });
    }

    this.dataSource.fetchData(this.paginator.pageIndex, this.paginator.pageSize, filters);
  }

  getComponentCategoryNames(): {name: string, value: number, }[] {
    return this.dictionaryHandler.getComponentCategoriesDictionary().map(x => ({ name: x.value, value: x.key }));
  }

  getSpecificComponentTypeNames(): {name: string, value: number, }[] {
    return this.componentTypeService.getAllComponentSubtypes().map(x => ({ name: x.name, value: x.id }));
  }

  navigateToComponent(component: PackagingComponentDto) {
    const componentTypeName = this.componentTypeService.getComponentTypeNameById(component.packagingComponentTypeId);
    if (component.packagingComponentCategoryId === PackagingComponentTypesEnum.Decoration) {
      this.navigationService.navigateToDecoration(componentTypeName, component.id ?? -1);
    } else {
      this.navigationService.navigateToComponent(componentTypeName, component.id ?? -1);
    }
  }

  selectAllComponents() {
    this.allItemsSelected = !this.allItemsSelected;
    this.selectedComponentIds = [];
    if (this.allItemsSelected) {
      for (const componentId of this.componentIds) {
        this.selectedComponentIds.push(componentId);
      }
    }
    this.packagingPartsSelected.emit(this.selectedComponentIds);
  }

  isItemSelected(item: PackagingComponentDto) {
    if (this.selectedComponentIds && this.selectedComponentIds.length > 0) {
      return this.selectedComponentIds.includes(item.id ?? -1);
    }
  }

  setMultipleItemsSelection(checked: boolean, item: PackagingComponentDto) {
    if (checked) {
      this.selectedComponentIds.push(item.id ?? -1);
    } else {
      const index = this.selectedComponentIds.indexOf(item.id ?? -1);
      this.selectedComponentIds.splice(index, 1);
    }
    this.packagingPartsSelected.emit(this.selectedComponentIds);
  }

  setSingleItemSelection(checked: boolean, item: PackagingComponentDto) {
    if (checked) {
      this.selectedComponentIds = [item.id ?? -1];
    } else {
      const index = this.selectedComponentIds.indexOf(item.id ?? -1);
      this.selectedComponentIds.splice(index, 1);
    }
    this.packagingPartsSelected.emit(this.selectedComponentIds);
  }

  private openDeleteDialog(components: number[]) {
    const dialogData: DeleteDialogData = {
      dialogHeader: this.translateService.translate('component.deleteComponentsHeader'),
      dialogText: this.translateService.translate('component.deleteComponentsText')
    };
    const dialogRef = this.dialog.open(DeleteItemDialogComponent, getDialogConfig(dialogData, '500px'));
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.DELETE) {
        this.bulkDeleteComponents(components);
      }
    });
  }

  private bulkDeleteComponents(componentIds: number[]) {
    this.spinner.show();
    const deletePermamently = this.isRecyclingBinSelected;
    const toDeleteDto = new DeletedEntitiesDto();
    toDeleteDto.deleted = [];
    for (const componentId of componentIds) {
      if (componentId != null) {
        toDeleteDto.deleted.push({ id: componentId, version: 0 });
      }
    }
    this.deleteBackendSubscription = this.componentApiService.deletePackagingComponent(toDeleteDto, deletePermamently, true).subscribe({
      next: _ => {
        this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.getDeleteComponentSuccessData(), '500px'));
        this.refreshDataSourceAfterChange(value => {
          return !componentIds.includes(value.id ?? -1);
        });
        this.spinner.hide();
      },
      error: e => {
        if (e.status === 403) {
          this.showDeletionImpossibleDialog();
        } else {
          this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.getDeleteComponentErrorData(), '500px'));
        }
        this.spinner.hide();
      },
    });
    this.resetSelection();
    this.packagingPartsSelected.emit(this.selectedComponentIds);
  }

  private getDeleteComponentSuccessData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.success'),
      messages: [this.translateService.translate('dataManagement.directory.dialog.removal.componentDeletedSuccess')], icon: 'info'
    };
  }

  private getDeleteComponentErrorData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.error'),
      messages: [this.translateService.translate('dataManagement.directory.dialog.removal.componentDeletedError')], icon: 'error'
    };
  }

  private showDeletionImpossibleDialog() {
    const data: SimpleDialogData = {
      title: this.translateService.translate('common.text.error'),
      messages: [this.translateService.translate('packagingUnit.messages.componentInUse')],
      icon: 'error'
    };
    const alertDialogConfig = getDialogConfig(data, '350px');
    this.dialog.open(SimpleAlertDialogComponent, alertDialogConfig);
  }

  getTagsSettingFunction(isEditing: boolean) {
    return (id: number, tagIds: number[]) => isEditing ?
      this.componentApiService.setComponentTags(id, tagIds) :
      this.componentApiService.addComponentTags(id, tagIds);
  }

  getPackagingPartFunction() {
    return (id: number) => this.componentApiService.getPackagingComponentById(id);
  }

  emptyRecyclingBin() {
    if (!this.isRecyclingBinSelected) {
      console.log('PackagingUnitsOverviewComponent: Recycling bin must be selected prior to emptying.');
      return;
    }
    this.bulkDeleteComponents(this.dataSource.data.filter(x => x.id != null).map(x => x.id ?? -1));
  }

  private refreshDataSourceAfterChange(filterPredicate: (value: PackagingComponentDto) => boolean) {
    if (this.dataSource instanceof PaginationDataSource) {
      this.refreshDataSource();
    } else {
      this.dataSource.data = this.dataSource.data.filter(filterPredicate);
    }
  }

  restorePackagingPart() {
    this.restorePackagingComponents(this.selectedComponentIds);
  }

  restoreAllPackagingComponents() {
    this.restorePackagingComponents(this.dataSource.data.filter(x => x.id).map(x => x.id ?? -1));
  }

  private restorePackagingComponents(componentIds: number[]) {
    if (!this.isRecyclingBinSelected) {
      console.log('PackagingComponentsOverviewComponent: Recycling bin must be selected prior to restoring.');
      return;
    }
    for (const componentId of componentIds) {
      this.refreshDataSourceAfterChange(value => {
         return value.id !== componentId;
      });
      if (componentId == null) { return; }
      this.restoreSubscription = this.directoryApiService.restorePackagingComponentFromRecyclingBin(
        componentId).subscribe(_ => {
          window.location.reload();
        });
    }
  }

  movePackagingPart(rootFolder: DirectoryDto, unreachableFolders: DirectoryDto[]) {
    const data = { rootFolder, unreachableFolders };
    const dialogConfig = getDialogConfig(data, '500px');
    const dialogRef = this.dialog.open(SelectLocationDialogComponent, dialogConfig);
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.REJECT) { return; }
      const observables = this.selectedComponentIds.map(componentId => {
        if (componentId != null) {
          return this.directoryApiService.moveComponent(result.data.id, componentId);
        }
        return of(null);
      });
      this.moveBackendSubscription = forkJoin(observables).subscribe(_ => {
        this.dataChanged.emit();
        this.refreshDataSourceAfterChange(value => this.selectedComponentIds.includes(value.id ?? -1));
      });
    });
  }

  copyPackagingPart(rootFolder: DirectoryDto, unreachableFolders: DirectoryDto[]) {
    const data = { rootFolder, unreachableFolders };
    const dialogConfig = getDialogConfig(data, '500px');
    const dialogRef = this.dialog.open(SelectLocationDialogComponent, dialogConfig);
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.REJECT) { return; }
      const observables = this.selectedComponentIds.map(componentId => {
        if (componentId != null) {
          return this.directoryApiService.copyComponent(result.data.id, componentId);
        }
        return of(null);
      });
      this.copyBackendSubscription = forkJoin(observables).subscribe({
        next: _ => this.dataChanged.emit(),
        error: _ => this.spinner.hide(),
      });
    });
  }

  deletePackagingPart(itemsToDelete = this.selectedComponentIds) {
    const observables = itemsToDelete.filter(componentId => componentId != null).map(componentId => {
      return this.componentApiService.getComponentPermissions(componentId);
    });
    this.permissionsSubscription = forkJoin(observables).subscribe((permissionsList: PermissionTypeDto[]) => {
      if (permissionsList.filter(x => x.delete === false).length > 0) {
        this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.getDeleteNotAllowedData(this.translateService), '500px'));
      } else {
        this.openDeleteDialog(itemsToDelete);
      }
    });
  }

  exportPackagingPart() {
    this.spinner.show();
    const dialogConfig = getDialogConfig({ componentIds: this.selectedComponentIds }, '50%');
    this.doExport(dialogConfig);
  }

  getComponentTypeImage(componentTypeId: number) {
    return this.componentTypeService.getComponentTypeImage(componentTypeId);
  }

  comparePackagingPart() {}

  editPackagingPartQuantity() {}

  resetSelection() {
    this.selectedComponentIds = [];
    this.allItemsSelected = false;
    this.packagingPartsSelected.emit(this.selectedComponentIds);
  }

  downloadPackagingComponentsJson() {
    const packagingComponentsToDownload: { id: number, version: number }[] = [];
    this.selectedComponentIds.forEach(id => {
      if (id != null) { packagingComponentsToDownload.push({ id: id, version: 0 }); }
    });
    this.jsonSubscription = this.componentApiService.downloadPackagingComponentsJson(packagingComponentsToDownload).subscribe(zipFile => {
      const zipUrl = window.URL.createObjectURL(zipFile);
      window.open(zipUrl, '_blank', 'noreferrer');
    });
  }

  private async doExport(dialogConfig: MatDialogConfig) {
    const exportProfilesFunction = () => this.importExportApiService.getPackagingComponentExportProfiles();
    const exportFunction = (componentId: number, principalId: number, profileId?: number) =>
      this.importExportApiService.exportComponents(componentId, principalId, profileId);
    super.exportItems(dialogConfig, exportProfilesFunction, exportFunction);
    this.resetSelection();
  }

  onPageChanged(event: PageEvent) {
    if (this.dataSource instanceof PaginationDataSource) {
      this.dataSource.fetchData(event.pageIndex, event.pageSize);
    }
  }

  onSortChanged(event: Sort) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (event.direction) {
        this.dataSource.sort(this.getPaginationFieldName(event.active), event.direction == "asc");
      } else {
        this.dataSource.removeSorting();
      }
    }
  }

  getPaginationFieldName(column: string) {
    switch (column) {
      case "id": return PackagingComponentPaginationFields.Id;
      case "tags": return PackagingComponentPaginationFields.Tag;
      case "packagingComponentCategoryName": return PackagingComponentPaginationFields.PackagingComponentCategory;
      case "articleName": return PackagingComponentPaginationFields.ArticleName;
      case "articleNumber": return PackagingComponentPaginationFields.ArticleNumber;
      case "directoryName": return PackagingComponentPaginationFields.DirectoryId;
      case "manufacturer": return PackagingComponentPaginationFields.Manufacturer;
      case "packagingComponentSubtypeName": return PackagingComponentPaginationFields.PackagingComponentSubtype;
    }

    return "";
  }

  getCaller(columnName: string) {
    if (this.isPaginatorVisible) {
      return this.getPaginationFieldName(columnName);
    }

    return columnName;
  }
}
